import { Box, Typography, MenuItem } from "@mui/material";
import Grid from "@mui/material/Grid";
import FileUpload from "../../../../../../components/FileUpload";
import { useState } from "react";
import { string } from "../../../../../../constants/string";
import Input from "../../../../../../components/Input";
import { usePolicyStore } from "../../../../../../store/usePolicyStore";
import { useClaimFormStore } from "../../../../../../store/useClaimFormStore";
import ButtonComp from "../../../../../../components/Button";
import { validateForm } from "../../../../../../utils/formValidation";

export default function Step2Form({
  handlePolicyInfoNext,
  handleBack,
}: {
  handlePolicyInfoNext: (data: any) => void;
  handleBack: () => void;
}) {
  const setPolicyInfo = useClaimFormStore((state) => state.setPolicyInfo);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showErrors, setShowErrors] = useState(false);
  const storedPolicyInfo = useClaimFormStore((state) => state.policyInfo);
  const [policyInfoDetails, setPolicyInfoDetails] = useState<{
    policyNumber?: string;
    policyType?: string;
    policyStatus?: string;
    productHistory?: string;
    mainMemberName: string;
    mainMemberContactDetails?: string;
  }>({
    policyNumber: "",
    policyType: "",
    policyStatus: "",
    productHistory: "None",
    mainMemberName: "",
    mainMemberContactDetails: "",
    ...(storedPolicyInfo || {}),
  });

  const policyData = usePolicyStore((state) => state.policyData);
  const setPolicyData = usePolicyStore((state) => state.setPolicyData);
  const addDocument = useClaimFormStore((state) => state.addDocument);
  const { documents } = useClaimFormStore();
  const fileName = documents["policy_document"]?.name ?? "No file chosen";

  const policyNumber =
    policyData?.policy_number || policyData?.mainMember?.policyNumber || "";
  const policyType = policyData?.policy_type || policyData?.mainMember?.productName || "";
  const policyStatus = policyData?.policy_status || policyData?.mainMember?.archiveStatus || "";
  const memberName =
    policyData?.member_name ||
    [policyData?.mainMember?.firstName, policyData?.mainMember?.surname].filter(Boolean).join(" ") ||
    policyData?.mainMember?.name ||
    "";
  const memberContactDetails =
    policyData?.member_contact_number ||
    policyData?.mainMember?.cellNumber ||
    policyData?.mainMember?.homeTel ||
    policyData?.mainMember?.workTel ||
    "";
  const productHistoryOptions = Array.isArray(policyData?.product_history)
    ? policyData?.product_history || []
    : [];

  const handleChange = (field: string, value: string) => {
    const updated = {
      ...policyInfoDetails,
      [field]: value,
    };
    setPolicyInfoDetails(updated);
  };

  const handleNext = () => {
    setShowErrors(true);
    if (fileName === "No file chosen") {
      setErrors({
        policyDocument: "Please upload the document.",
      });
      return;
    }
    const formDataToValidate = {
      ...policyInfoDetails,
      policyDocument: fileName,
    };

    const { isValid, errors } = validateForm(formDataToValidate, ["policyDocument"]);

    if (!isValid) {
      setErrors(errors);
      return;
    }
    setErrors({});
    const updated = {
      ...policyInfoDetails,
      policyNumber,
      policyType,
      policyStatus,
      mainMemberContactDetails: memberContactDetails,
      mainMemberName: memberName,
      productHistory: policyInfoDetails?.productHistory || "",
    };
    setPolicyInfo(updated);
    handlePolicyInfoNext(updated);
  };

  return (
    <Box component="form" noValidate autoComplete="off">
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Input fullWidth label="Policy Number" variant="outlined" value={policyNumber} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Input fullWidth label="Policy Type" variant="outlined" value={policyType} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Input fullWidth label="Policy Status" variant="outlined" value={policyStatus} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          {policyInfoDetails?.productHistory === "" ? (
            <Input fullWidth label="Product History" variant="outlined" value="None" />
          ) : (
            <Input
              select
              fullWidth
              label="Product History"
              variant="outlined"
              value={policyInfoDetails?.productHistory || ""}
              onChange={(e: any) => {
                const value = e.target.value;
                handleChange("productHistory", value);
                setPolicyInfo({
                  ...(storedPolicyInfo || {}),
                  productHistory: value,
                });
              }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected: any) =>
                  selected ? selected : <span style={{ color: "#aaa" }}>Select Product History</span>,
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: "#ffffff",
                      borderRadius: "12px",
                      maxHeight: "300px",
                    },
                  },
                },
              }}
            >
              {productHistoryOptions.map((item: string, index: number) => (
                <MenuItem value={item} key={index}>
                  {item}
                </MenuItem>
              ))}
            </Input>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Input fullWidth label="Main Member Name" variant="outlined" value={memberName} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            fullWidth
            label="Main Member Contact Details"
            variant="outlined"
            value={memberContactDetails}
          />
        </Grid>

      </Grid>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="body2" fontWeight={600} mt={2} textAlign={"left"}>
          {string.claims.rmaPolicyReport}
        </Typography>
        <FileUpload
          inputId="rma-policy"
          fileName={fileName}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              if (file.size <= 2 * 1024 * 1024) {
                addDocument("policy_document", file);
                setErrors((prev) => ({ ...prev, policyDocument: "" }));
              } else {
                setErrors((prev) => ({
                  ...prev,
                  policyDocument: "File must be less than 2MB.",
                }));
              }
            }
          }}
        />
        {errors.policyDocument && (
          <Typography
            sx={{
              color: "error.main",
              fontSize: "0.8rem",
              textAlign: "left",
            }}
          >
            {errors.policyDocument}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          py: 6,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2 },
        }}
      >
        <ButtonComp
          variant="outlined"
          height={40}
          fontSize="16px"
          width={200}
          onClick={() => {
            setPolicyData(null);
            handleBack();
          }}
        >
          {string.claims.back}
        </ButtonComp>
        <ButtonComp variant="contained" height={40} fontSize="16px" width={200} onClick={handleNext}>
          {string.claims.next}
        </ButtonComp>
      </Box>
    </Box>
  );
}



